import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './Council.css';

const Council = () => {
    const { isAdmin, userData } = useAuth();
    const { showToast } = useToast();
    const [councilMembers, setCouncilMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [imageViewer, setImageViewer] = useState({ show: false, url: '' });
    const [exporting, setExporting] = useState(false);

    const CLOUDINARY_CLOUD_NAME = 'dblffn98e';
    
    // Constants for PDF header
    const MINISTRY_NAME = "White Doves Ministry";
    const MINISTRY_ADDRESS = "P.O. Box 43844 - 00100, Nairobi, Kenya";
    const MINISTRY_PHONE = "+254 740086845";
    const MINISTRY_EMAIL = "info.whitedovesministry@gmail.com";
    const LOGO_PATH = "/Logo1.png";

    // ====================
    // ACADEMIC YEAR CALCULATION
    // ====================
    
    const getAcademicYear = (date = new Date()) => {
        const currentYear = date.getFullYear();
        const currentMonth = date.getMonth(); // 0 = January, 7 = August
        
        // Academic year runs from August to April
        // If month is August (7) or later, it's the start of a new academic year
        // If month is May (4) or June (5) or July (6), it's the end of the academic year
        
        let startYear, endYear;
        
        if (currentMonth >= 7) { // August to December
            startYear = currentYear;
            endYear = currentYear + 1;
        } else if (currentMonth >= 0 && currentMonth <= 3) { // January to April
            startYear = currentYear - 1;
            endYear = currentYear;
        } else { // May, June, July (between academic years)
            // This is the transition period, treat it as the end of the previous academic year
            startYear = currentYear - 1;
            endYear = currentYear;
        }
        
        return {
            startYear,
            endYear,
            display: `${startYear}/${endYear}`,
            startDate: new Date(startYear, 7, 1), // August 1st
            endDate: new Date(endYear, 3, 30) // April 30th
        };
    };

    // Get current academic year
    const currentAcademicYear = getAcademicYear();

    // Get previous academic year
    const getPreviousAcademicYear = (academicYear) => {
        const prevStartYear = academicYear.startYear - 1;
        const prevEndYear = academicYear.endYear - 1;
        return {
            startYear: prevStartYear,
            endYear: prevEndYear,
            display: `${prevStartYear}/${prevEndYear}`,
            startDate: new Date(prevStartYear, 7, 1),
            endDate: new Date(prevEndYear, 3, 30)
        };
    };

    // Get next academic year
    const getNextAcademicYear = (academicYear) => {
        const nextStartYear = academicYear.startYear + 1;
        const nextEndYear = academicYear.endYear + 1;
        return {
            startYear: nextStartYear,
            endYear: nextEndYear,
            display: `${nextStartYear}/${nextEndYear}`,
            startDate: new Date(nextStartYear, 7, 1),
            endDate: new Date(nextEndYear, 3, 30)
        };
    };

    // Format academic year for display
    const formatAcademicYear = (academicYear) => {
        return academicYear.display;
    };

    // Check if a date falls within an academic year
    const isDateInAcademicYear = (date, academicYear) => {
        const dateObj = new Date(date);
        return dateObj >= academicYear.startDate && dateObj <= academicYear.endDate;
    };

    // Get all academic years for dropdown or display
    const getAcademicYearRange = (count = 5) => {
        const years = [];
        let current = getAcademicYear();
        
        // Add current year
        years.push(current);
        
        // Add previous years
        for (let i = 1; i < count; i++) {
            const prevYear = getPreviousAcademicYear(years[years.length - 1]);
            years.push(prevYear);
        }
        
        // Sort chronologically (oldest first)
        return years.reverse();
    };

    const academicYears = getAcademicYearRange(5);

    useEffect(() => {
        loadCouncilMembers();
    }, []);

    const loadCouncilMembers = () => {
        const councilQuery = query(
            collection(db, 'admins'),
            orderBy('createdAt', 'asc')
        );

        const unsubscribe = onSnapshot(councilQuery, (snapshot) => {
            const members = [];
            snapshot.forEach(doc => {
                members.push({ id: doc.id, ...doc.data() });
            });
            
            members.sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
                const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
                return dateA - dateB;
            });

            setCouncilMembers(members);
            setLoading(false);
        }, (error) => {
            console.error('Error loading council members:', error);
            showToast('Error loading council members', 'error');
            setLoading(false);
        });

        return unsubscribe;
    };

    const getInitials = (fullName) => {
        if (!fullName) return '?';
        return fullName.split(' ').map(word => word.charAt(0).toUpperCase()).join('');
    };

    // WhatsApp function
    const handleWhatsApp = (phone, name) => {
        if (!phone || phone === 'Not provided') {
            showToast(`No phone number available for ${name}`, 'error');
            return;
        }

        const formattedPhone = phone.replace(/\D/g, '');
        
        let whatsappNumber = formattedPhone;
        if (formattedPhone.startsWith('0')) {
            whatsappNumber = '254' + formattedPhone.substring(1);
        } else if (!formattedPhone.startsWith('254')) {
            whatsappNumber = '254' + formattedPhone;
        }

        const whatsappUrl = `https://wa.me/${whatsappNumber}`;
        window.open(whatsappUrl, '_blank');
    };

    const handleDelete = async (memberId) => {
        if (!isAdmin) return;

        const result = await Swal.fire({
            title: 'Remove Council Member?',
            text: 'This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, remove!'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'admins', memberId));
                showToast('Council member removed successfully!', 'success');
            } catch (error) {
                console.error('Error removing council member:', error);
                showToast('Error removing council member', 'error');
            }
        }
    };

    const handleImageClick = (url) => {
        if (url) {
            setImageViewer({ show: true, url });
        }
    };

    const getPhotoUrl = (member) => {
        return member.photoURL || member.profilePhoto || 
               (member.photoPath ? `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/${member.photoPath}` : null);
    };

    // ====================
    // PDF EXPORT FUNCTION
    // ====================

    const addMinistryHeader = async (doc) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        
        try {
            const logoResponse = await fetch(LOGO_PATH);
            const logoBlob = await logoResponse.blob();
            const logoDataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(logoBlob);
            });
            doc.addImage(logoDataUrl, 'PNG', 14, 5, 30, 30);
        } catch (error) {
            console.error('Error loading logo:', error);
        }
        
        doc.setFillColor(16, 52, 166);
        doc.rect(0, 0, pageWidth, 40, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text(MINISTRY_NAME, pageWidth / 2, 20, { align: 'center' });
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
       doc.text(`"Then John gave this testimony: 'I saw the Spirit come down from heaven as a dove and remain on him.' - John 1:32"`, pageWidth / 2, 30, { align: 'center' });
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`${MINISTRY_ADDRESS} | Tel: ${MINISTRY_PHONE} | Email: ${MINISTRY_EMAIL}`, pageWidth / 2, 48, { align: 'center' });
        
        doc.setDrawColor(16, 52, 166);
        doc.setLineWidth(0.5);
        doc.line(20, 52, pageWidth - 20, 52);
        
        return 55;
    };

    const addFooter = (doc) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`White Doves Ministry - Council Report`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
    };

    const exportToPDF = async () => {
        if (councilMembers.length === 0) {
            showToast('No council members to export', 'error');
            return;
        }

        setExporting(true);

        try {
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            let yPos = await addMinistryHeader(doc);

            // Title
            doc.setFontSize(16);
            doc.setTextColor(16, 52, 166);
            doc.setFont('helvetica', 'bold');
            doc.text('COUNCIL MEMBERS REPORT', pageWidth / 2, yPos + 10, { align: 'center' });
            
            yPos += 25;
            
            // Report Info
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            doc.setFont('helvetica', 'normal');
            doc.text(`Generated By: ${userData?.fullName || 'Admin'}`, 20, yPos);
            doc.text(`Date: ${new Date().toLocaleString()}`, 20, yPos + 8);
            doc.text(`Academic Year: ${formatAcademicYear(currentAcademicYear)}`, 20, yPos + 16);
            doc.text(`Total Council Members: ${councilMembers.length}`, 20, yPos + 24);

            // Council Members Table
            const tableData = councilMembers.map(member => [
                member.fullName || 'Council Member',
                member.role || 'Member',
                member.phone || 'Not provided',
                member.department || 'N/A',
               
            ]);

            doc.autoTable({
                startY: yPos + 32,
                head: [['Name', 'Role', 'Phone', 'Department']],
                body: tableData,
                theme: 'striped',
                headStyles: { 
                    fillColor: [16, 52, 166], 
                    textColor: 255, 
                    fontSize: 10,
                    fontStyle: 'bold'
                },
                bodyStyles: { fontSize: 9 },
                margin: { left: 20, right: 20 },
                foot: [[
                    'TOTAL: ' + councilMembers.length,
                    '', '', '', ''
                ]],
                footStyles: { 
                    fillColor: [240, 240, 240], 
                    fontStyle: 'bold', 
                    fontSize: 9 
                }
            });

            addFooter(doc);
            doc.save(`council-members-${currentAcademicYear.display}-${new Date().toISOString().split('T')[0]}.pdf`);
            showToast('Council report exported successfully!', 'success');

        } catch (error) {
            console.error('Error exporting PDF:', error);
            showToast('Failed to export PDF', 'error');
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center" style={{ padding: '50px' }}>
                <i className="fas fa-spinner fa-spin fa-3x" style={{ color: '#1034A6' }}></i>
                <p style={{ marginTop: '20px' }}>Loading council members...</p>
            </div>
        );
    }

    return (
        <div className="council-page">
            <div className="council-header">
                <div>
                    <h1 className="council-title">DOVES COUNCIL</h1>
                    <span className="council-year">
                        {formatAcademicYear(currentAcademicYear)}
                    </span>
                </div>
                {councilMembers.length > 0 && (
                    <button
                        className="council-export-btn"
                        onClick={exportToPDF}
                        disabled={exporting}
                    >
                        <i className="fas fa-file-pdf"></i>
                        {exporting ? 'Exporting...' : 'Export to PDF'}
                    </button>
                )}
            </div>

            {/* Academic Year Selector */}
            <div className="council-year-selector">
                <div className="council-academic-info">
                    <i className="fas fa-calendar-alt"></i>
                    <span>
                        Academic Year: <strong>{formatAcademicYear(currentAcademicYear)}</strong>
                        <span className="council-academic-dates">
                            ({currentAcademicYear.startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - 
                            {currentAcademicYear.endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})
                        </span>
                    </span>
                </div>
                <div className="council-academic-badge">
                    <i className="fas fa-info-circle"></i>
                    {new Date().getMonth() >= 7 ? 'New Academic Year Started' : 
                     new Date().getMonth() >= 0 && new Date().getMonth() <= 3 ? 'Academic Year in Progress' : 
                     'Academic Year Wrapping Up'}
                </div>
            </div>

            <div className="council-section">
                <div className="council-section-header">
                    <h2>
                        <i className="fas fa-user-shield"></i>
                        Ministry Council
                        <span className="council-count-badge">{councilMembers.length}</span>
                    </h2>
                </div>

                <div className="council-table-container">
                    <table className="council-table">
                        <thead>
                            <tr>
                                <th>Photo</th>
                                <th>Name</th>
                                <th>Role</th>
                                <th>Phone</th>
                                {isAdmin && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {councilMembers.length === 0 ? (
                                <tr>
                                    <td colSpan={isAdmin ? 5 : 4} className="council-empty">
                                        <i className="fas fa-user-slash"></i>
                                        <h3>No Council Members</h3>
                                        <p>Add council members to get started</p>
                                    </td>
                                </tr>
                            ) : (
                                councilMembers.map(member => {
                                    const photoUrl = getPhotoUrl(member);
                                    
                                    return (
                                        <tr key={member.id}>
                                            <td>
                                                {photoUrl ? (
                                                    <img
                                                        src={photoUrl}
                                                        alt={member.fullName}
                                                        className="council-photo"
                                                        onClick={() => handleImageClick(photoUrl)}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.style.display = 'none';
                                                            e.target.parentElement.innerHTML = `
                                                                <div class="council-photo-placeholder">
                                                                    ${getInitials(member.fullName)}
                                                                </div>
                                                            `;
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="council-photo-placeholder">
                                                        {getInitials(member.fullName)}
                                                    </div>
                                                )}
                                            </td>
                                            <td><strong>{member.fullName || 'Council Member'}</strong></td>
                                            <td>
                                                <span className="council-role">{member.role || 'Member'}</span>
                                            </td>
                                            <td>
                                                <span className="council-phone">{member.phone || 'Not provided'}</span>
                                                <button
                                                    className="council-whatsapp-btn"
                                                    onClick={() => handleWhatsApp(member.phone, member.fullName)}
                                                    disabled={!member.phone || member.phone === 'Not provided'}
                                                    title={`Send WhatsApp message to ${member.fullName}`}
                                                >
                                                    <i className="fab fa-whatsapp"></i>
                                                </button>
                                            </td>
                                            {isAdmin && (
                                                <td>
                                                    <button
                                                        className="council-delete-btn"
                                                        onClick={() => handleDelete(member.id)}
                                                        title="Remove council member"
                                                    >
                                                        <i className="fas fa-trash-alt"></i>
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Image Viewer Modal */}
            {imageViewer.show && (
                <div className="council-image-viewer" onClick={() => setImageViewer({ show: false, url: '' })}>
                    <img src={imageViewer.url} alt="Council Member" />
                </div>
            )}
        </div>
    );
};

export default Council;
